import { ChangeEvent, useState } from "react";
import { Form } from "react-bootstrap";
import TextField from "@components/FormsV2/TextField/TextField";
import MarkdownTextAreaV2 from "@components/FormsV2/MarkdownTextAreaV2";
import type { TextBlockFormValues } from "../types";

const CONTENT_PLACEHOLDER = "Content supporting Markdown";

export interface TextBlockFormProps {
  id: string;
  initialValues: TextBlockFormValues;
  isSubmitting: boolean;
  onSubmit: (values: TextBlockFormValues) => void;
  // Server-derived merge id, shown read-only on Edit. Omitted on New.
  slug?: string;
}

export const TextBlockForm = ({
  id,
  initialValues,
  isSubmitting,
  onSubmit,
  slug,
}: TextBlockFormProps): JSX.Element => {
  const [values, setValues] = useState<TextBlockFormValues>(initialValues);

  const set = <K extends keyof TextBlockFormValues>(
    key: K,
    v: TextBlockFormValues[K]
  ) => setValues((prev) => ({ ...prev, [key]: v }));

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    onSubmit(values);
  };

  return (
    <Form id={id} data-testid={id} onSubmit={handleSubmit}>
      <div className="row">
        <div className="col-sm-8">
          <Form.Label htmlFor="text-block-name">Name</Form.Label>
          <TextField
            label={null}
            id="text-block-name"
            value={values.name}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              set("name", e.target.value)
            }
            required
          />
        </div>
        {slug !== undefined && (
          <div className="col-sm-4">
            <Form.Label>Merge ID</Form.Label>
            <div className="text-muted">{slug}</div>
            <small className="small text-muted d-block">
              Auto-generated from the name; used to reference this block in
              merge data.
            </small>
          </div>
        )}
      </div>

      <div className="row" style={{ paddingTop: "16px" }}>
        <div className="col">
          <Form.Label>Content (Supports Markdown)</Form.Label>
          <MarkdownTextAreaV2
            alwaysEdit
            value={values.mergeBlock}
            placeholder={CONTENT_PLACEHOLDER}
            onChange={(md: string) => set("mergeBlock", md)}
          />
        </div>
      </div>
    </Form>
  );
};

export default TextBlockForm;
